import { addDays } from 'date-fns'
import { FILTER_FIELD_DEFINITIONS, isDueDateOperator } from './fieldDefinitions'
import type { FilterOperator, FilterValue, TaskFilter } from '@/lib/types/filter'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { CustomFieldValueDocument } from '@/lib/db/rxdb/hooks/entities/custom-field-values'

export interface FilterContext {
  taskLabelMap?: Map<string, Set<string>> | null
  /** Custom field values: Map<taskId, Map<fieldDefId, valueDoc>> */
  customFieldValueMap?: Map<string, Map<string, CustomFieldValueDocument>> | null
  /** Cycle membership: Map<taskId, Set<cycleId>> */
  taskCycleMap?: Map<string, Set<string>> | null
}

/**
 * Returns true if the task passes the filters. Ungrouped filters all apply
 * (AND); grouped ones pass when every filter of at least one group does
 * (design 9b OR groups). Within a filter, multiple values use OR logic.
 * Filters without values are ignored, and so is a group with none set.
 */
export function taskPassesFilters(
  task: TaskDocument,
  filters: ReadonlyArray<TaskFilter>,
  context: FilterContext = {},
): boolean {
  const groups = new Map<number, Array<TaskFilter>>()
  for (const filter of filters) {
    if (filter.values.length === 0) continue
    if (filter.group === undefined) {
      if (!evaluateFilter(task, filter, context)) return false
      continue
    }
    const list = groups.get(filter.group)
    if (list) list.push(filter)
    else groups.set(filter.group, [filter])
  }
  if (groups.size === 0) return true
  for (const group of groups.values()) {
    if (group.every((f) => evaluateFilter(task, f, context))) return true
  }
  return false
}

function evaluateFilter(
  task: TaskDocument,
  filter: TaskFilter,
  context: FilterContext,
): boolean {
  // Custom field filter (cf_ prefix)
  if (filter.fieldKey.startsWith('cf_')) {
    return evaluateCustomFieldFilter(task, filter, context)
  }

  const fieldDef = FILTER_FIELD_DEFINITIONS[filter.fieldKey]
  if (!fieldDef) return true

  // Special handling for join-based fields
  if (filter.fieldKey === 'labels') {
    return evaluateLabelFilter(task, filter, context)
  }

  if (filter.fieldKey === 'cycle') {
    return evaluateCycleFilter(task, filter, context)
  }

  if (filter.fieldKey === 'due' && isDueDateOperator(filter.operator)) {
    return evaluateDueDate(task.dueDate, filter.operator, Number(filter.values[0]))
  }

  const taskValue = fieldDef.getTaskValue(task)

  if (fieldDef.fieldType === 'select') {
    return evaluateSelectPredicate(filter.operator, filter.values, taskValue)
  }

  if (fieldDef.fieldType === 'text') {
    return evaluateTextPredicate(
      filter.operator,
      filter.values[0] as string,
      taskValue as string,
    )
  }

  return true
}

function evaluateSelectPredicate(
  operator: FilterOperator,
  filterValues: Array<FilterValue>,
  taskValue: FilterValue | Array<FilterValue> | null,
): boolean {
  // Ordered values (priority): at least the lowest one picked.
  if (operator === 'at_least') {
    const min = Math.min(...filterValues.map(Number))
    return typeof taskValue === 'number' && taskValue >= min
  }

  const taskValues = Array.isArray(taskValue) ? taskValue : [taskValue]
  const hasMatch = taskValues.some((v) =>
    filterValues.includes(v as FilterValue),
  )

  switch (operator) {
    case 'includes':
      return hasMatch
    case 'excludes':
      return !hasMatch
    default:
      return true
  }
}

/** `day` is the start of the chosen day; tasks without a due date never match. */
function evaluateDueDate(
  dueDate: number | null | undefined,
  operator: FilterOperator,
  day: number,
): boolean {
  if (Number.isNaN(day)) return true
  if (dueDate == null) return false
  const nextDay = addDays(day, 1).getTime()
  switch (operator) {
    case 'before':
      return dueDate < day
    case 'on':
      return dueDate >= day && dueDate < nextDay
    case 'after':
      return dueDate >= nextDay
    default:
      return true
  }
}

function evaluateTextPredicate(
  operator: FilterOperator,
  filterText: string,
  taskText: string,
): boolean {
  const lower = (taskText ?? '').toLowerCase()
  const filterLower = (filterText ?? '').toLowerCase()

  switch (operator) {
    case 'contains':
      return lower.includes(filterLower)
    case 'does_not_contain':
      return !lower.includes(filterLower)
    case 'equals':
      return lower === filterLower
    case 'not_equal':
      return lower !== filterLower
    default:
      return true
  }
}

function evaluateLabelFilter(
  task: TaskDocument,
  filter: TaskFilter,
  context: FilterContext,
): boolean {
  const taskLabelIds = context.taskLabelMap?.get(task.id)
  const hasMatch = taskLabelIds
    ? filter.values.some((id) => taskLabelIds.has(id as string))
    : false

  switch (filter.operator) {
    case 'includes':
      return hasMatch
    case 'excludes':
      return !hasMatch
    default:
      return true
  }
}

function evaluateCycleFilter(
  task: TaskDocument,
  filter: TaskFilter,
  context: FilterContext,
): boolean {
  const taskCycleIds = context.taskCycleMap?.get(task.id)
  const hasMatch = taskCycleIds
    ? filter.values.some((id) => taskCycleIds.has(id as string))
    : false

  switch (filter.operator) {
    case 'includes':
      return hasMatch
    case 'excludes':
      return !hasMatch
    default:
      return true
  }
}

function evaluateCustomFieldFilter(
  task: TaskDocument,
  filter: TaskFilter,
  context: FilterContext,
): boolean {
  const fieldDefId = filter.fieldKey.slice(3) // remove 'cf_' prefix
  const taskValues = context.customFieldValueMap?.get(task.id)
  const valueDoc = taskValues?.get(fieldDefId)

  if (!valueDoc) {
    // No value set - 'excludes' passes, everything else fails
    return filter.operator === 'excludes'
  }

  // Get the raw value from the document
  const rawValue = valueDoc.stringValue ?? valueDoc.numberValue ?? valueDoc.dateValue

  // Select operators (for list-type fields)
  if (filter.operator === 'includes' || filter.operator === 'excludes') {
    const hasMatch = filter.values.some((v) => String(v) === String(rawValue))
    return filter.operator === 'includes' ? hasMatch : !hasMatch
  }

  // Text operators (for string-type fields)
  if (filter.operator === 'contains' || filter.operator === 'does_not_contain') {
    return evaluateTextPredicate(filter.operator, filter.values[0] as string, String(rawValue ?? ''))
  }

  // Number operators
  if (filter.operator === 'greater_than' || filter.operator === 'less_than') {
    const numValue = Number(rawValue)
    const filterNum = Number(filter.values[0])
    if (Number.isNaN(numValue) || Number.isNaN(filterNum)) return true
    return filter.operator === 'greater_than' ? numValue > filterNum : numValue < filterNum
  }

  // Date operators
  if (filter.operator === 'before' || filter.operator === 'after') {
    const dateValue = Number(rawValue)
    const filterDate = Number(filter.values[0])
    if (!dateValue || !filterDate) return true
    return filter.operator === 'before' ? dateValue < filterDate : dateValue > filterDate
  }

  // Equals / not_equal (generic)
  if (filter.operator === 'equals') {
    return String(rawValue) === String(filter.values[0])
  }
  if (filter.operator === 'not_equal') {
    return String(rawValue) !== String(filter.values[0])
  }

  return true
}
